import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Substitui `useState` pra valores de filtro/paginação que devem viver
 * na URL — torna o estado compartilhável (link), restaurável (back/reload)
 * e reset-friendly (default não polui a query string).
 *
 * Convenção: valores iguais ao `defaultValue` são removidos do URL, então
 * `?status=&page=1` nunca aparece — só os filtros realmente aplicados.
 *
 * @example
 *   const [search, setSearch] = useUrlState('search', '')
 *   const [page, setPage] = useUrlState('page', 1)
 *   // chamar setPage(1) ao trocar filtro continua sendo responsabilidade
 *   // do componente (igual era com useState) — mantemos explicito.
 */
export function useUrlState<T extends string | number>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get(key)

  let value: T
  if (raw === null) {
    value = defaultValue
  } else if (typeof defaultValue === 'number') {
    const parsed = Number(raw)
    value = (Number.isFinite(parsed) ? parsed : defaultValue) as T
  } else {
    value = raw as T
  }

  const setValue = useCallback(
    (next: T) => {
      // IMPORTANTE: ler de globalThis.location.search ao inves do `searchParams`
      // closure-captured. Quando o componente chama dois setters no mesmo
      // handler (ex.: setActiveFilter('active'); setPage(1)), o segundo setter
      // recebe via callback o searchParams do render anterior — sem enxergar
      // a mudanca do primeiro — e acabaria reescrevendo o URL sem ela.
      // Como react-router atualiza location sincronamente via history, ler do
      // location.search dentro do setter garante o estado mais fresco.
      const updated = new URLSearchParams(globalThis.location.search)
      const isDefault =
        (typeof defaultValue === 'number' && next === defaultValue) ||
        (typeof defaultValue === 'string' && (next === defaultValue || next === ''))
      if (isDefault) {
        updated.delete(key)
      } else {
        updated.set(key, String(next))
      }
      setSearchParams(updated, { replace: true })
    },
    [key, defaultValue, setSearchParams],
  )

  return [value, setValue]
}
